import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Navbar } from "@/components/layout/Navbar";
import { PageTransition } from "@/components/layout/PageTransition";
import ScrollToTop from "@/components/layout/ScrollToTop";
import { ProtectedRoute } from "@/components/admin/ProtectedRoute";
import { useEffect } from "react";
import { fetchAPI } from "@/lib/api";
import { HelmetProvider } from "react-helmet-async";
import Index from "./pages/Index";

// Lazy load pages
import Circuits from "./pages/Circuits";
import Blog from "./pages/Blog";
import StoryDetail from "./pages/StoryDetail";
import Gallery from "./pages/Gallery";
import Baelleaves from "./pages/Baelleaves";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import NotFound from "./pages/NotFound";
import TourPlans from "./pages/TourPlans";
import TourDetail from "./pages/TourDetail";

// Admin Imports
import { AdminLayout } from "@/components/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import CircuitsManager from "@/pages/admin/CircuitsManager";
import StoriesManager from "@/pages/admin/StoriesManager";
import GalleryManager from "@/pages/admin/GalleryManager";
import MetadataManager from "@/pages/admin/MetadataManager";
import LegalManager from "@/pages/admin/LegalManager";
import AdminManager from "@/pages/admin/AdminManager";
import InquiriesManager from "@/pages/admin/InquiriesManager";
import TourPlansManager from "@/pages/admin/TourPlansManager";
import Login from "@/pages/admin/Login";

const queryClient = new QueryClient();

// Helper component to conditionally render Navbar
const NavbarWrapper = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin-panel");
  return !isAdminRoute ? <Navbar /> : null;
};



const App = () => {
  useEffect(() => {
    // Setup Auth DB on first load (dev only convenience)
    fetchAPI("?type=setup_auth").catch(console.error);
    
    // Fetch and apply global settings (meta tags)
    fetchAPI("?type=settings").then((settings: any) => {
        if (settings.og_image) {
            const ogImage = document.querySelector('meta[property="og:image"]');
            if (ogImage) ogImage.setAttribute("content", settings.og_image);
            
            const twitterImage = document.querySelector('meta[name="twitter:image"]');
            if (twitterImage) twitterImage.setAttribute("content", settings.og_image);
        }
    }).catch(console.error);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
  
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <ScrollToTop />
            <NavbarWrapper />
            <AnimatePresence mode="wait">
                <Routes>
                  {/* Public Routes */}
                  <Route path="/home" element={<Navigate to="/" replace />} />
                  <Route path="/" element={<PageTransition><Index /></PageTransition>} />
                  <Route path="/circuits" element={<PageTransition><Circuits /></PageTransition>} />
                  <Route path="/stories" element={<PageTransition><Blog /></PageTransition>} />
                  <Route path="/stories/:slug" element={<PageTransition><StoryDetail /></PageTransition>} />
                  <Route path="/gallery" element={<PageTransition><Gallery /></PageTransition>} />
                  <Route path="/baelleaves" element={<PageTransition><Baelleaves /></PageTransition>} />
                  <Route path="/privacy-policy" element={<PageTransition><PrivacyPolicy /></PageTransition>} />
                  <Route path="/terms-of-service" element={<PageTransition><TermsOfService /></PageTransition>} />
                  <Route path="/tour-plans" element={<PageTransition><TourPlans /></PageTransition>} />
                  <Route path="/tour-plans/:slug" element={<PageTransition><TourDetail /></PageTransition>} />
                  
                  {/* Admin Auth */}
                  <Route path="/admin-panel/login" element={<Login />} />
  
                  {/* Protected Admin Routes */}
                  <Route element={<ProtectedRoute />}>
                    <Route path="/admin-panel" element={<AdminLayout />}>
                      <Route path="dashboard" element={<AdminDashboard />} />
                      <Route path="circuits" element={<CircuitsManager />} />
                      <Route path="stories" element={<StoriesManager />} />
                      <Route path="gallery" element={<GalleryManager />} />
                      <Route path="tour-plans" element={<TourPlansManager />} />
                      <Route path="metadata" element={<MetadataManager />} />
                      <Route path="legal" element={<LegalManager />} />
                      <Route path="admins" element={<AdminManager />} />
                      <Route path="inquiries" element={<InquiriesManager />} />
                      <Route index element={<AdminDashboard />} />
                    </Route>
                  </Route>
  
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />
                </Routes>
            </AnimatePresence>
          </BrowserRouter>
        </TooltipProvider>
      </HelmetProvider>
    </QueryClientProvider>
  );
};



export default App;
